/**
 * Chunk-load-error self-heal (gh #825).
 *
 * Next.js content-hashes every client chunk per build, and each Cloud Run
 * revision serves only its OWN `_next/static`. When a deploy shifts traffic,
 * a browser still running the previous build requests a chunk URL that the new
 * revision 404s, and React throws `ChunkLoadError` into the error boundary.
 *
 * The boundary's `reset()` CANNOT recover this: it re-renders the same
 * already-loaded client bundle without re-fetching the document, so the chunk
 * URL is still a 404 and it fails identically. `<Link href="/">` is a
 * client-side navigation on that same dead bundle and fails too. Only a hard
 * document reload picks up the new build — which is why gh #825 was a ~30-min
 * lockout for a user who kept clicking "Try again" (UAT deploy
 * webapp-foltrigg-00038-jg8, 2026-06-19T17:50:53Z).
 *
 * The guard is the load-bearing part. An unguarded "reload on chunk error"
 * turns a deploy that genuinely serves a broken bundle into an infinite reload
 * loop. We stamp the attempt in sessionStorage and refuse to reload again
 * within RELOAD_COOLDOWN_MS: a loop re-enters the boundary in ~1s (blocked,
 * error UI shown), while a genuine second deploy later in the same tab session
 * is far outside the window (allowed). sessionStorage — not localStorage — so
 * the stamp dies with the tab.
 */

/** Re-arm window. A reload loop re-enters well inside this; a later deploy does not. */
export const RELOAD_COOLDOWN_MS = 30_000;

const STORAGE_KEY = 'foltrigg:chunk-reload-at';

/**
 * Webpack/Turbopack surface this failure with several different shapes
 * depending on how the chunk was requested (static import vs. lazy
 * `import()`), so match the message text rather than the error class:
 *   - "Failed to load chunk /_next/static/chunks/2v676c0rlilr-.js from module 67174"  (gh #825, Turbopack)
 *   - "Loading chunk 123 failed."                                                      (webpack)
 *   - "ChunkLoadError: Loading CSS chunk 5 failed"                                     (webpack, CSS)
 */
const CHUNK_ERROR_PATTERN =
  /(failed to load chunk|loading (css )?chunk .* failed|chunkloaderror)/i;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as Error).name ?? '';
  const message = (error as Error).message ?? '';
  return CHUNK_ERROR_PATTERN.test(name) || CHUNK_ERROR_PATTERN.test(message);
}

export interface ChunkReloadDeps {
  /** Injected for tests; defaults to the real tab-scoped store. */
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  now?: () => number;
  reload?: () => void;
}

/**
 * Reload once to pick up the new build if `error` is a chunk-load failure.
 * Returns true when a reload was actually triggered.
 *
 * Storage access is wrapped: Safari private mode and hardened browser configs
 * throw on sessionStorage. We deliberately DO NOT reload when the store is
 * unreachable — without a durable stamp there is no loop guard, and an
 * unguarded reload loop is worse than the error screen the user already has.
 */
export function attemptChunkReload(
  error: unknown,
  deps: ChunkReloadDeps = {},
): boolean {
  if (!isChunkLoadError(error)) return false;
  if (typeof window === 'undefined') return false;

  const now = deps.now ?? Date.now;
  const reload = deps.reload ?? (() => window.location.reload());

  let store: Pick<Storage, 'getItem' | 'setItem'>;
  try {
    store = deps.storage ?? window.sessionStorage;
    const last = Number(store.getItem(STORAGE_KEY) ?? '0');
    // NaN (hand-edited/corrupt value) must not read as "0 = long ago" and
    // re-enable the loop, so treat any non-finite stamp as "just reloaded".
    if (!Number.isFinite(last) || now() - last < RELOAD_COOLDOWN_MS) return false;
    store.setItem(STORAGE_KEY, String(now()));
  } catch {
    return false;
  }

  reload();
  return true;
}
